import React, { useState, useEffect } from "react";
import { SkeletonLoader, colors2 } from "oolib";
import styled from "styled-components";
import {
  UI_BODY_SM,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_BODY_SEMIBOLD_DF,
} from "oolib/dist/v2/components/Typo2";
import { DashboardWidgetShell } from "../../comps/DashboardWidgetShell";
import { StatusPill } from "../../comps/StatusPill";
import { useDetailPanel } from "../../comps/ActivePanelContext";
import { useGetFeatureHealth, useGetTagDetail } from "../../hooks/useGetLensData";
import { ConfidenceIndicator } from "../comps/ConfidenceIndicator";
import { TransparencyPanel } from "../comps/TransparencyPanel";
import { TagDetailSidebar } from "../comps/TagDetailSidebar";
import { WIDGET_STYLE, WIDGET_BODY_STYLE, getStatusTheme } from "../constants";
import { Heart } from "@phosphor-icons/react";

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
`;

const FeatureRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 1.2rem 0;
  border-bottom: 1px solid ${colors2.grey5};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

const FeatureRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const FeatureDescription = styled(UI_BODY_SM)`
  color: ${colors2.grey60};
  line-height: 1.5;
`;

export function FeatureHealthBlock({ period, locality, showTransparency }) {
  const { data, isLoading } = useGetFeatureHealth({ period, locality });
  const { selected: selectedFeature, select: selectFeature, deselect: deselectFeature, isOpen: featurePanelOpen } = useDetailPanel("lenses:feature-health");

  // Close sidebar when period changes
  useEffect(() => { deselectFeature(); }, [period]);

  const { data: tagDetail, isLoading: tagDetailLoading } = useGetTagDetail({
    tagId: selectedFeature?.tagIds?.[0],
    period,
    mode: "all",
    locality,
    enabled: !!selectedFeature,
  });

  return (
    <>
    <DashboardWidgetShell
      titleJSX={
        <TitleWrapper>
          <Heart size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF style={{ display: "flex", gap: "0.5rem" }}>
            Trending product features health
          </UI_BODY_SEMIBOLD_SM_DF>
        </TitleWrapper>
      }
      style={WIDGET_STYLE}
      bodyStyle={WIDGET_BODY_STYLE}
    >
      {isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={{ padding: "8px 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <SkeletonLoader style={{ height: 14, width: `${35 + Math.random() * 25}%`, borderRadius: 4 }} />
                <SkeletonLoader style={{ height: 22, width: 70, borderRadius: 12 }} />
              </div>
              <SkeletonLoader style={{ height: 10, width: "80%", borderRadius: 4, marginTop: 6 }} />
            </div>
          ))}
        </div>
      )}
      {!isLoading && (!data?.features?.length) && (
        <UI_BODY_SM style={{ color: colors2.grey40 }}>No feature data available yet.</UI_BODY_SM>
      )}
      {!isLoading && (
        <FeatureList>
          {(data?.features || []).map((feature) => {
            const healthTheme = getStatusTheme("health", feature.health);
            return (
              <FeatureRow
                key={feature.name}
                onClick={() => selectFeature(feature)}
              >
                <FeatureRowTop>
                  <UI_BODY_SEMIBOLD_DF style={{ color: colors2.grey90 }}>{feature.name}</UI_BODY_SEMIBOLD_DF>
                  <StatusPill theme={healthTheme} />
                </FeatureRowTop>
                {feature.description && (
                  <FeatureDescription>{feature.description}</FeatureDescription>
                )}
              </FeatureRow>
            );
          })}
          {showTransparency && data?.coverage && (
            <>
              <ConfidenceIndicator confidence={data.coverage.confidence} summary={data.coverage.summary} />
              <TransparencyPanel warnings={data.coverage.warnings} />
            </>
          )}
        </FeatureList>
      )}
    </DashboardWidgetShell>

    {(() => {
      const featureTheme = getStatusTheme("health", selectedFeature?.health);
      return (
        <TagDetailSidebar
          isOpen={featurePanelOpen}
          onClose={deselectFeature}
          isLoading={tagDetailLoading}
          title={selectedFeature?.name}
          mentionsCount={tagDetail?.annotations?.length}
          description={tagDetail?.description}
          sources={tagDetail?.sources}
          mentions={(tagDetail?.annotations || []).map((a) => ({ tone: a.sentiment, text: a.text, fragment: a.fragment }))}
          showSentimentPolarity
          pill={<StatusPill theme={featureTheme} as="span" />}
          primaryColor={featureTheme.accent}
          glowColor={featureTheme.glow}
        />
      );
    })()}
    </>
  );
}
